import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { first } from 'rxjs/operators';
import { FeatureFlags } from 'src/app/models/feature-flag.model';
import { User } from 'src/app/models/users.model';

const EMPTY_FLAGS: FeatureFlags = {} as FeatureFlags;

interface FlagsCacheEntry {
  /** Signed-in uid the flags were merged for; null when fetched signed out */
  uid: string | null;
  flags: Promise<FeatureFlags>;
}

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  /**
   * Single-flight session cache keyed by the signed-in user: flags are fetched
   * once per user and every caller (including many list rows mounting at once)
   * shares the same promise. Keying by uid means a read made on the login page
   * (signed out) can never be served to the user who signs in afterwards, and a
   * sign-out / different sign-in in the same tab starts fresh — per-user
   * overrides always come from the current user's own document.
   * Flags rarely change mid-session; call refresh() to force a re-fetch.
   */
  private cache: FlagsCacheEntry | null = null;

  constructor(
    private afs: AngularFirestore,
    private afAuth: AngularFireAuth,
  ) {}

  /** Forget the cached flags — the next read re-fetches from Firestore */
  refresh(): void {
    this.cache = null;
  }

  private async getFlags(): Promise<FeatureFlags> {
    // authState only emits once Firebase has restored the persisted session, so
    // a caller that fires during app start-up still resolves against the real
    // user rather than a not-yet-known one.
    const auth = await this.afAuth.authState.pipe(first()).toPromise();
    const uid = auth?.uid ?? null;

    if (!this.cache || this.cache.uid !== uid) {
      const entry = { uid } as FlagsCacheEntry;
      entry.flags = this.fetchFlags(uid).catch((error) => {
        // Don't poison the cache with a failed fetch — retry on next read
        console.error('Error fetching feature flags:', error);
        if (this.cache === entry) this.cache = null;
        return EMPTY_FLAGS;
      });
      this.cache = entry;
    }

    return this.cache.flags;
  }

  private async fetchFlags(uid: string | null): Promise<FeatureFlags> {
    const globalDoc = await this.afs.doc<FeatureFlags>('configs/featureFlags').get().toPromise();
    const flags = globalDoc?.data() || EMPTY_FLAGS;

    if (!uid) return flags;

    const userDoc = await this.afs.doc<User>(`users/${uid}`).get().toPromise();
    const userFlags = userDoc?.data()?.featureFlags || EMPTY_FLAGS;
    return { ...flags, ...userFlags };
  }

  async isEnabled(key: keyof FeatureFlags): Promise<boolean> {
    const flags = await this.getFlags();
    return !!flags[key];
  }

  async getValue<T>(key: keyof FeatureFlags): Promise<T | undefined> {
    const flags = await this.getFlags();
    return flags[key] as T;
  }
}
